// Company Detection and Path Resolution Utilities
// All data now uses company-specific paths under companies/{companyId}/

import { ref, get } from "firebase/database";
import { getDatabaseInstance } from '../services/recruiterApi';

export const LEGACY_COMPANY_ID = 'legacy';
export const LEGACY_COMPANY_NAME = 'Creative Personnel Technology';

// ============================================================================
// PERFORMANCE: Company Detection Cache
// Caches detectUserCompany results with 5-minute TTL to avoid repeated DB calls
// ============================================================================
const companyCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const getCachedCompany = (userId) => {
  const cached = companyCache.get(userId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }
  return null;
};

const setCachedCompany = (userId, data) => {
  companyCache.set(userId, { data, timestamp: Date.now() });
};

// Export for testing/manual cache clearing
export const clearCompanyCache = () => {
  companyCache.clear();
};

// Detect if user is legacy or belongs to a specific company
export const detectUserCompany = async (userId) => {
  // PERFORMANCE: Check cache first
  const cached = getCachedCompany(userId);
  if (cached) {
    console.log('[detectUserCompany] Cache hit for user:', userId);
    return cached;
  }

  const database = getDatabaseInstance();

  try {
    // Check if user has company mapping
    const mappingRef = ref(database, `userCompanyMappings/${userId}`);
    const mappingSnapshot = await get(mappingRef);

    if (mappingSnapshot.exists()) {
      const mapping = mappingSnapshot.val();
      console.log('[detectUserCompany] Found mapping for user:', userId, mapping);

      if (mapping.companyId) {
        // Get user data from company structure
        const companyUserRef = ref(database, `companies/${mapping.companyId}/users/${userId}`);
        const userSnapshot = await get(companyUserRef);

        const result = {
          companyId: mapping.companyId,
          companyName: mapping.companyName || LEGACY_COMPANY_NAME,
          isLegacy: false,
          userData: userSnapshot.exists() ? userSnapshot.val() : null
        };
        setCachedCompany(userId, result);
        return result;
      }
    }

    // Fallback: check company-specific path for legacy company
    const legacyCompanyUserRef = ref(database, `companies/${LEGACY_COMPANY_ID}/users/${userId}`);
    const legacySnapshot = await get(legacyCompanyUserRef);

    if (legacySnapshot.exists()) {
      console.log('[detectUserCompany] Found user in legacy company:', userId);
      const result = {
        companyId: LEGACY_COMPANY_ID,
        companyName: LEGACY_COMPANY_NAME,
        isLegacy: false,
        userData: legacySnapshot.val()
      };
      setCachedCompany(userId, result);
      return result;
    }

    // No userCompanyMappings entry and not in legacy path.
    // Scanning /companies to find the user would be an O(companies) full read that
    // will time out at scale and is blocked by Phase 0 default-deny rules.
    // The correct fix is to ensure userCompanyMappings is populated by running:
    //   scripts/migrations/2026-05-07-backfill-mappings.js
    console.warn(
      '[detectUserCompany] No mapping found for user:', userId,
      '— returning null. Run scripts/migrations/2026-05-07-backfill-mappings.js ' +
      'to populate userCompanyMappings for all existing users.'
    );
    setCachedCompany(userId, null); // Cache negative result
    return null; // User not provisioned — caller must handle
  } catch (error) {
    console.error('Error detecting user company:', error);
    return null;
  }
};

// Get the correct database path based on company
// All paths now use company-specific structure
export const getDataPath = (companyId, path) => {
  // Always use company-specific paths - no more legacy fallback
  return `companies/${companyId}/${path}`;
};

// Get users path for a company
export const getUsersPath = (companyId) => {
  return getDataPath(companyId, 'users');
};

// Get time records path for a company
export const getTimeRecordsPath = (companyId) => {
  return getDataPath(companyId, 'timeRecords');
};

// Get employee status path for a company
export const getEmployeeStatusPath = (companyId) => {
  return getDataPath(companyId, 'employeeStatus');
};

// Get settings path for a company
export const getSettingsPath = (companyId) => {
  return getDataPath(companyId, 'settings');
};

// Check if company exists
export const companyExists = async (companyId) => {
  const database = getDatabaseInstance();
  try {
    const companyRef = ref(database, `companies/${companyId}`);
    const snapshot = await get(companyRef);
    return snapshot.exists();
  } catch (error) {
    console.error('Error checking company existence:', error);
    return false;
  }
};

// Get company info
export const getCompanyInfo = async (companyId) => {
  const database = getDatabaseInstance();
  try {
    const companyRef = ref(database, `companies/${companyId}`);
    const snapshot = await get(companyRef);

    if (snapshot.exists()) {
      return {
        id: companyId,
        ...snapshot.val(),
        isLegacy: false
      };
    }

    // Return default info for legacy company
    if (companyId === LEGACY_COMPANY_ID) {
      return {
        id: LEGACY_COMPANY_ID,
        name: LEGACY_COMPANY_NAME,
        isLegacy: false
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting company info:', error);
    return null;
  }
};